import { Modal } from '@mui/base';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArticleIcon from '@mui/icons-material/Article';
import EditIcon from '@mui/icons-material/BorderColorOutlined';
import ClearIcon from '@mui/icons-material/Clear';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIconButton from '../../components/DeleteIconButton';
import ImageIcon from '@mui/icons-material/Image';
import LinkIcon from '@mui/icons-material/Link';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import VideoCallIcon from '@mui/icons-material/VideoCall';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import { Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, FormControlLabel, Grid, IconButton, Link, List, ListItem, ListItemIcon, ListItemSecondaryAction, ListItemText, Tooltip, Typography } from '@mui/material';
import { Switch } from '@mui/material';
import { grey } from '@mui/material/colors';
import { makeStyles } from '@mui/styles';
import _ from 'lodash';
import { useSnackbar } from 'notistack';
import { default as React } from 'react';

import { DataAPIContext, UPDATE } from '../../api';
import { UPLOAD } from '../../api/dataProvider';
import FormActionButtons from '../../components/FormActionButtons';
import StyledFilledTextField from '../../components/StyledFilledTextField';
import { url2filename, useIsWidthUp } from '../../utils';
import { checkFileType } from '../../utils/file';
// import UploadDocumentButton from './UploadDocumentButton';
import validURL from '../../utils/validators/url';
import DegreeSlider from './DegreeSlider';

const FILE = 1;
const LINK = 2;

const useStyles = makeStyles((theme) => ({
    arrowButton: {
        width: '25px',
        height: '25px',
        display: 'flex',
        justifyContent: 'center',
        border: `solid 1px ${grey[400]}`,
        borderRadius: '5px',
        cursor: 'pointer',
        background: 'transparent',
        color: theme.palette.primary.main,
        alignItems: 'center',
    },
    dragHandle: {
        cursor: 'grab',
    },
    editModal: {
        position: 'fixed',
        width: 350,
        backgroundColor: theme.palette.background.paper,
        boxShadow: theme.shadows[5],
        borderRadius: '5px',
        padding: theme.spacing(2),
        top: '45%',
        left: '50%',
        transform: 'translate(-50%,-50%)',
        zIndex: 10,
        maxHeight: '80vh',
        overflowY: 'auto',
        overflowX: 'hidden',
        marginTop: '1rem',
        [theme.breakpoints.down('sm')]: {
            top: '50%',
        },
    },
    editIconWrapper: {
        marginLeft: theme.spacing(2),
    },
    editIcon: {
        color: grey[600],
    },
    labelContainer: {
        backgroundColor: '#EEEEEE',
        display: 'flex',
        justifyContent: 'space-between',
        borderRadius: '8px',
        borderBottom: 'solid #747475 1px',
        margin: '0.25rem',
        width: '127px',
        padding: '0.35rem 0.5rem',
        [theme.breakpoints.down('sm')]: {
            width: '80px',
        },
    },
    inputIcon: {
        color: '#999999',
        fontSize: '16px !important',
        transform: 'translateY(4px)'
    }
}));




const RenderDocumentLabel = ({ label, link, is360 }) => {
    const isSmallScreen = !useIsWidthUp('sm')
    let file_ext = label
    let max_label_length = isSmallScreen ? 5 : 14;
    let label_string = label.split('.')[0] + '.'
    let label_length = label_string?.length

    label_string = label_string?.slice(0, max_label_length) + (label_length > max_label_length ? '...' : '')

    const classes = useStyles();
    if (label) {
        let ext = label.split('.').pop()
        file_ext = ext === label ? null : ext;
    }
    const iconMap = {
        'image': is360 ? <AddPhotoAlternateIcon className={classes.inputIcon} /> : <ImageIcon className={classes.inputIcon} />,
        'audio': <VolumeUpIcon className={classes.inputIcon} />,
        'video': <VideoCallIcon className={classes.inputIcon} />,
        'document': <ArticleIcon className={classes.inputIcon} />,
        'link': <LinkIcon className={classes.inputIcon} />,
    };

    const iconTitleMap = {
        'image': is360 ? '360° image' : 'Image',
        'audio': 'Audio',
        'video': 'Video',
        'document': 'Document',
        'link': 'Link',
    };

    const fileType = checkFileType(label);
    const IconComponent = fileType ? iconMap[fileType] : null;


    if (isSmallScreen) {
        return <></>
    }

    // The filename tooltip wraps only the link so it doesn't also open over the type icon's own tooltip.
    return <Grid className={classes.labelContainer} style={{ width: isSmallScreen ? '150px' : '200px', overflowX: 'hidden' }}>
        <Tooltip title={label?.length > max_label_length ? label : ''}>
            <Link href={link} target="_blank">
                <Typography noWrap={true}>
                    {label_string}
                    {file_ext}
                </Typography>
            </Link>
        </Tooltip>
        {IconComponent && (
            <Tooltip title={iconTitleMap[fileType] || ''}>
                {IconComponent}
            </Tooltip>
        )}
    </Grid>
}

const RenderNameLabel = ({ label }) => {
    const isSmallScreen = !useIsWidthUp('sm')
    // text overflow on small screens works fine
    let max_label_length = isSmallScreen ? 100 : 20;
    let label_string = label?.slice(0, max_label_length) + (label?.length > max_label_length ? '...' : '')
    const classes = useStyles();
    return <Tooltip title={label?.length > max_label_length ? label : ''}>
        <Grid className={classes.labelContainer} style={{ width: isSmallScreen ? 'calc(100vw - 13rem)' : '200px' }}>
            <Typography noWrap={true}>
                {label_string}
            </Typography>
        </Grid>
    </Tooltip>
}


const getComparableState = (state) => {
    const a = {
        ...state,
    };
    delete a._changed;
    return JSON.stringify(a);
};

export const LinkModalEditableField = (props) => {
    const [isEditing, setIsEditing] = React.useState(!!props.open);
    const [localState, setLocalState] = React.useState(props.value);
    const isChanged = getComparableState(props.value) !== getComparableState(localState);
    const [validUrlError, setValidUrlError] = React.useState(false);
    const label = props.value.name || props.value.url || '<Please change the name>';
    const url = props.value.url;

    const onConfirm = (ev) => {
        props.onChange && props.onChange(localState);
        setIsEditing(false);
    };

    const onChange = (ev, confirm) => {
        setLocalState((prev) => ({ ...prev, [ev.target.name]: ev.target.value }));
        if (confirm) {
            props.onChange && props.onChange(ev.target.value);
            setIsEditing(false);
        }
    };

    const onKeyDown = (e) => {
        if (e.keyCode === 13) {
            e.preventDefault();
            onConfirm();
        }
    };

    const onSwitchChange = (ev) => {
        setLocalState((prev) => ({ ...prev, [ev.target.name]: ev.target.checked }));
    };

    const handleURLOnBlur = () => {
        if (validURL(localState.url)) {
            setValidUrlError(false);
        } else {
            setValidUrlError(true);
        }
    };
    const onClose = () => {
        setIsEditing(false);
        setLocalState(props.value);
        props.onDiscard();
        props.setDisableButtons(false);
    };

    const onOpen = () => {
        setIsEditing(true);
        props.setDisableButtons(true);
    };

    return (
        <>
            <Grid container justifyContent="flex-start" alignItems="center">
                <RenderNameLabel label={label} />
                <RenderDocumentLabel label={url} link={url} />
                <Tooltip title="Edit">
                    <span>
                        <IconButton disabled={props.disabled || props.disableButtons} size="small" onClick={onOpen} className={props.classes.editIconWrapper}>
                            <EditIcon className={props.classes.editIcon} />
                        </IconButton>
                    </span>
                </Tooltip>
                <DeleteIconButton fontSize="medium" disabled={props.syncActive || props.disableButtons || props.disabled} onClick={props.onDelete} />
            </Grid>
            <Modal open={isEditing} onClose={onClose} className={props.classes.editModal}>
                <Grid container direction="row" spacing={1}>
                    {props.title ? (
                        <Grid item xs={11}>
                            <Typography variant="h6">{props.title}</Typography>
                        </Grid>
                    ) : null}
                    <Grid item xs={1} onClick={onClose} style={{ cursor: 'pointer' }}>
                        <Tooltip title="Close">
                            <ClearIcon />
                        </Tooltip>
                    </Grid>
                    <Grid item xs={12}>
                        <Grid container direction="row" spacing={2}>
                            <Grid item xs={12}>
                                <StyledFilledTextField
                                    disabled={props.disabled}
                                    label="Label"
                                    name="name"
                                    value={localState.name}
                                    variant="filled"
                                    size="small"
                                    onChange={onChange}
                                    inputProps={{
                                        onKeyDown: onKeyDown,
                                    }}
                                    fullWidth
                                    autoFocus
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <StyledFilledTextField
                                    disabled={props.disabled}
                                    label="URL"
                                    name="url"
                                    value={localState.url}
                                    variant="filled"
                                    size="small"
                                    inputProps={{
                                        onKeyDown: onKeyDown,
                                    }}
                                    onChange={onChange}
                                    fullWidth
                                    onBlur={handleURLOnBlur}
                                    error={validUrlError}
                                    helperText={validUrlError && 'Please enter a valid URL.'}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <FormControlLabel disabled={props.disabled} label="Deep link" control={<Switch name="deep_link" color="primary" disabled={props.disabled} checked={localState.deep_link} onChange={onSwitchChange} />} />
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item xs={12}>
                        {/* CP-8423: [CANCEL] [SAVE] bottom-right, SAVE with the save icon (also when adding a link). */}
                        <FormActionButtons
                            mode="edit"
                            onSubmit={onConfirm}
                            submitDisabled={!localState.url || !localState.name || !isChanged || validUrlError}
                            onCancel={onClose}
                        />
                    </Grid>
                </Grid>
            </Modal>
        </>
    );
};

export const ActivityDocumentsDialog = (props) => {
    const [replace, setReplace] = React.useState(true);

    const onClose = () => {
        props.onClose && props.onClose();
    };

    const onSave = () => {
        props.onSave && props.onSave(replace);
    };

    const onSelectAll = () => {
        props.onChange && props.onChange(props.documents.map((x) => ({ ...x, _selected: !x._duplicate })));
    };

    const onSelectNone = () => {
        props.onChange && props.onChange(props.documents.map((x) => ({ ...x, _selected: false })));
    };

    const onToggleItem = (ev) => {
        props.onChange && props.onChange(props.documents.map((x) => (+x.id === +ev.target.name ? { ...x, _selected: !x._selected } : x)));
    };

    return (
        <Dialog fullWidth="true" maxWidth="md" open={props.open} onClose={onClose} style={{ zIndex: 10000, marginTop: '10vh' }}>
            <DialogTitle>
                <Grid container direction="row">
                    <Grid item xs={10}>
                        Copy Activity Documents/Links to Custom On-Trip Documents/Links
                    </Grid>
                    <Grid item xs={2} container justifyContent="flex-end">
                        {/* The dialog sits at zIndex 10000, above the tooltip's default layer. */}
                        <Tooltip title="Close" PopperProps={{ style: { zIndex: 10001 } }}>
                            <IconButton size="small" onClick={onClose}>
                                <CloseIcon />
                            </IconButton>
                        </Tooltip>
                    </Grid>
                </Grid>
            </DialogTitle>
            <DialogContent>
                <DialogContentText>Select the documents/links you would like to be copied from activity</DialogContentText>
                <Button color="primary" onClick={onSelectAll}>
                    Select all
                </Button>
                <Button color="primary" onClick={onSelectNone}>
                    Select none
                </Button>
                <List dense>
                    {(props.documents || []).map((x) => (
                        <ListItem key={x.id}>
                            <ListItemIcon>
                                <Checkbox
                                    edge="start"
                                    onChange={onToggleItem}
                                    name={x.id}
                                    checked={x._selected}
                                    disabled={x._duplicate}
                                    tabIndex="-1"
                                    disableRipple
                                    color="primary"
                                />
                            </ListItemIcon>
                            <ListItemText
                                primary={
                                    <>
                                        {x.document.type === LINK ? 'Link' : 'File'}: {props.isSmallScreen ? x.document.name.slice(0, 12) : x.document.name}
                                        {props.isSmallScreen && x.document.name.length > 12 && '...'}
                                        {x._duplicate ? ' (already added)' : ''}
                                    </>
                                }
                            />
                            <ListItemSecondaryAction>
                                <Tooltip title="Open in new tab" PopperProps={{ style: { zIndex: 10001 } }}>
                                    <Link href={x.document.link} target="_blank">
                                        <OpenInNewIcon />
                                    </Link>
                                </Tooltip>
                            </ListItemSecondaryAction>
                        </ListItem>
                    ))}
                </List>
            </DialogContent>
            <DialogActions>
                {+props.existingDocumentsCount > 0 ? (
                    <FormControlLabel
                        sx={{ ml: 1 }}
                        control={<Checkbox checked={replace} color="primary" onChange={(ev) => setReplace(!replace)} />}
                        label={`Replace ${props.existingDocumentsCount} existing document${props.existingDocumentsCount > 1 ? 's' : ''}/link${props.existingDocumentsCount > 1 ? 's' : ''}`}
                    />
                ) : null}
                <div style={{ flex: '1 0 0' }} />
                {/* CP-8423: [CANCEL] [SAVE] bottom-right; CANCEL closes like the title's X. */}
                <FormActionButtons
                    mode="edit"
                    onSubmit={onSave}
                    submitDisabled={props.syncActive}
                    submitIcon={props.syncActive ? <CircularProgress size={20} color="inherit" /> : undefined}
                    onCancel={onClose}
                    size="medium"
                    sx={{ mr: 1, my: 1 }}
                />
            </DialogActions>
        </Dialog>
    );
};

export const CustomOntripDocument = (props) => {
    // const PENDING = 1;
    // const READY = 2;
    // const state = props.data.file ? PENDING : READY;
    const [localState, setLocalState] = React.useState(props.data);
    const isChanged = JSON.stringify(localState) !== JSON.stringify(props.data);
    const file_url = props.data.link ? url2filename(props.data.link) : '';
    const [isEditing, setIsEditing] = React.useState(false);
    const isSmallScreen = !useIsWidthUp('sm')

    const label = props.data.name;

    const onDelete = (ev) => {
        props.onDelete && props.onDelete(props.idx);
    };
    const onSave = async (ev, newLocalState) => {
        // The save itself reports success or failure once the server answers.
        if (newLocalState?.id) {
            await props.onSave(newLocalState, props.idx);
            return;
        }
        await props.onSave(localState, props.idx);
        setIsEditing(false);
    };
    const onKeyDown = (ev) => {
        if (ev.keyCode === 13) {
            onSave();
        }
    };
    const onMoveUp = (ev) => {
        if (props.idx > 0) {
            props.onOrderChange(props.idx, props.idx - 1);
        } else {
            console.error("CustomOntripDocument: can't move document at index", props.idx, 'above zero');
        }
    };
    const onMoveDown = (ev) => {
        if (props.idx < props.maxIdx) {
            props.onOrderChange(props.idx, props.idx + 1);
        } else {
            console.error("CustomOntripDocument: can't move document at index", props.idx, 'below the max index', props.maxIdx);
        }
    };

    const onConfirm = async () => {
        // if (!localState.id) {
        //     props.processReplaceDocumentFile(localState.file, localState.sequence, localState.name);
        //     return;
        // }
        onSave();
    };

    const onLabelChange = (ev) => {
        setLocalState((prev) => ({ ...prev, name: ev.target.value }));
    };

    const on360DegreeChange = (ev, saveNow = false) => {
        setLocalState((prev) => {
            if (saveNow) {
                onSave('ev', { ...prev, is_360_degree: ev.target.checked });
            }
            return ({ ...prev, is_360_degree: ev.target.checked })
        });
    }
    // const onFileChange = (file) => {
    //     setLocalState((prev) => ({ file: file, link: file.name, name: prev.name, sequence: prev.sequence }));
    // };

    const onClose = () => {
        if (isChanged) {
            setLocalState(props.data)
        }
        setIsEditing(false);
        props.setDisableButtons(false);
    };

    const onOpen = () => {
        setIsEditing(true);
        props.setDisableButtons(true);
    };

    const handleDegreeSliderChange = (newValue) => {
        setLocalState((prev) => {
            return { ...prev, offset_360_degree: newValue }
        });
    }

    return (
        <Grid item xs={12} container direction="row" alignItems="center" flexWrap="nowrap">
            {!props.disabled && props?.showArrowButtons && (
                <Grid item container gap={'0.25rem'} flexDirection="col" width={isSmallScreen ? 120 : 65}>
                    <Tooltip title="Move up">
                        <button className={props.classes.arrowButton} style={{ color: (props.syncActive || props.idx === 0) && grey[400], cursor: (props.syncActive || props.idx === 0) && 'default' }} onClick={onMoveUp}>
                            <ArrowUpwardIcon style={{ fontSize: 16 }} />
                        </button>
                    </Tooltip>
                    <Tooltip title="Move down">
                        <button className={props.classes.arrowButton} style={{ color: (props.syncActive || props.idx === props.maxIdx) && grey[400], cursor: (props.syncActive || props.idx === props.maxIdx) && 'default' }} onClick={onMoveDown}>
                            <ArrowDownwardIcon style={{ fontSize: 16 }} />
                        </button>
                    </Tooltip>
                </Grid>)}
            <Grid item xs={'auto'} container justifyContent="flex-start" alignItems="center">
                <RenderNameLabel label={label} />
                {file_url ? (
                    <RenderDocumentLabel label={file_url} link={localState.link} is360={localState?.is_360_degree} />
                ) : (
                    'Uploading...'
                )}
                {/* <Tooltip title="360 degree image">
                    <Checkbox checked={!!localState?.is_360_degree} onChange={ev => on360DegreeChange(ev, true)}/>
                </Tooltip> */}
                <Tooltip title="Edit">
                    <span>
                        <IconButton size="small" disabled={props.disableButtons || props.disabled} onClick={onOpen} className={props.classes.editIconWrapper}>
                            <EditIcon className={props.classes.editIcon} />
                        </IconButton>
                    </span>
                </Tooltip>
                <DeleteIconButton fontSize="medium" disabled={props.syncActive || props.disableButtons || props.disabled} onClick={onDelete} />
            </Grid>
            <Modal open={isEditing} onClose={onClose} className={props.classes.editModal}>
                <Grid container direction="row" spacing={1}>
                    <Grid item xs={11}>
                        <Typography variant="h6">File details</Typography>
                    </Grid>
                    <Grid item xs={1} onClick={onClose} style={{ cursor: 'pointer' }}>
                        <Tooltip title="Close">
                            <ClearIcon />
                        </Tooltip>
                    </Grid>
                    <Grid item xs={12}>
                        <Grid container direction="row" spacing={2}>
                            <Grid item xs={12}>
                                <StyledFilledTextField
                                    disabled={props.disabled}
                                    label="Label"
                                    name="name"
                                    value={localState?.name}
                                    variant="filled"
                                    size="small"
                                    onChange={onLabelChange}
                                    inputProps={{
                                        onKeyDown: onKeyDown,
                                    }}
                                    fullWidth
                                    autoFocus
                                />
                            </Grid>
                            <Grid item xs={12} mb={0}>
                                <div style={{ backgroundColor: '#f0f0f0', borderRadius: '8px', borderBottom: 'solid #747475 1px', padding: '0.25rem 0.75rem 0.25rem 0.75rem' }}>
                                    <Typography color="#727272" fontSize={13}>
                                        File
                                    </Typography>
                                    <Typography>{file_url}</Typography>
                                </div>
                            </Grid>
                            {checkFileType(file_url) === 'image' && <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={!!localState?.is_360_degree}
                                            onChange={ev => on360DegreeChange(ev, false)}
                                        />
                                    }
                                    label="360 degree image"
                                />
                            </Grid>}
                            {localState?.is_360_degree &&
                                <Grid item xs={12} mb={2}>
                                    <DegreeSlider data={localState} onChange={handleDegreeSliderChange} />
                                </Grid>}
                        </Grid>
                    </Grid>
                    <Grid item xs={12}>
                        {/* CP-8423: [CANCEL] [SAVE] bottom-right, SAVE with the save icon. */}
                        <FormActionButtons
                            mode="edit"
                            onSubmit={onConfirm}
                            submitDisabled={!localState.name || !isChanged}
                            onCancel={onClose}
                        />
                    </Grid>
                </Grid>
            </Modal>
        </Grid>
    );
};

export const CustomOntripLink = (props) => {
    const [localState, setLocalState] = React.useState(props.data);
    const isSmallScreen = !useIsWidthUp('sm');

    const onDelete = (ev) => {
        props.onDelete && props.onDelete(props.idx);
    };

    const onSave = (state, action) => {
        if (props.existingDocs.filter((link) => link.type === 2 && link.url === state.url && link.id !== state.id).length > 0) {
            props.enqueueSnackbar('Duplicate link found, please check existing links', { variant: 'default', autoHideDuration: 3000 });
            props.onDelete && props.onDelete(props.idx);
            return;
        }
        if (!state) {
            state = localState;
        }
        // The save itself reports success or failure once the server answers.
        props.onSave && props.onSave(state, props.idx);
    };

    const onMoveUp = (ev) => {
        if (props.idx > 0) {
            props.onOrderChange(props.idx, props.idx - 1);
        } else {
            console.error("CustomOntripDocument: can't move document at index", props.idx, 'above zero');
        }
    };
    const onMoveDown = (ev) => {
        if (props.idx < props.maxIdx) {
            props.onOrderChange(props.idx, props.idx + 1);
        } else {
            console.error("CustomOntripDocument: can't move document at index", props.idx, 'below the max index', props.maxIdx);
        }
    };

    const onLinkChange = (update) => {
        const ulc = update.url.toString().toLowerCase();
        if (ulc.indexOf('http://') < 0 && ulc.indexOf('https://') < 0) {
            update.url = `http://${update.url}`;
        }
        setLocalState((prevState) => {
            const ns = {
                ...prevState,
                ...update,
                _changed: true,
            };
            setTimeout(() => onSave(ns, 'update'), 0);
            return ns;
        });
        props.onLinkClose && props.onLinkClose();
    };

    const onModalDiscard = () => {
        if (!props.data.url) {
            props.onDelete && props.onDelete(props.idx);
        }
    };

    return (
        <Grid item xs={12} container direction="row" alignItems="center" flexWrap="nowrap">
            {!props.disabled && props.showArrowButtons && (
                <Grid item container gap={'0.25rem'} flexDirection="col" width={isSmallScreen ? 120 : 65}>
                    <Tooltip title="Move up">
                        <button className={props.classes.arrowButton} style={{ color: (props.syncActive || props.idx === 0) && grey[400], cursor: (props.syncActive || props.idx === 0) && 'default' }} onClick={onMoveUp}>
                            <ArrowUpwardIcon style={{ fontSize: 16 }} />
                        </button>
                    </Tooltip>
                    <Tooltip title="Move down">
                        <button className={props.classes.arrowButton} style={{ color: (props.syncActive || props.idx === props.maxIdx) && grey[400], cursor: (props.syncActive || props.idx === props.maxIdx) && 'default' }} onClick={onMoveDown}>
                            <ArrowDownwardIcon style={{ fontSize: 16 }} />
                        </button>
                    </Tooltip>
                </Grid>
            )}
            <Grid item xs={'auto'} >
                <LinkModalEditableField
                    disabled={props.disabled}
                    classes={props.classes}
                    open={props.open}
                    title="Link details"
                    isSmallScreen={isSmallScreen}
                    onDiscard={onModalDiscard}
                    value={{ name: localState.name, url: localState.url, deep_link: localState.deep_link }}
                    onChange={onLinkChange}
                    syncActive={props.syncActive}
                    onDelete={onDelete}
                    disableButtons={props.disableButtons}
                    setDisableButtons={props.setDisableButtons}
                />
            </Grid>
        </Grid>
    );
};

export const CustomOntripDocumentList = (props) => {
    const dataAPI = React.useContext(DataAPIContext);
    const resource = 'tour_activity';
    const { docs, syncActive, setSyncActive } = props;
    const { enqueueSnackbar } = useSnackbar();
    const classes = useStyles();
    const [disableButtons, setDisableButtons] = React.useState(false);
    const isSmallScreen = !useIsWidthUp('sm');


    const maxOntripDocumentIdx = docs?.filter((d) => !d.file).length - 1;

    const onChange = (newDocs) => {
        props.onChange && props.onChange(newDocs);
    };

    const saveOntripDocumentsOrder = (newDocs) => {
        if (!newDocs) {
            newDocs = docs || [];
        }
        props.setNewDocsAdded(prev => {
            if (!prev) {
                return [props.activity.id]
            }
            return [...prev, props.activity.id]
        })
        dataAPI(UPDATE, resource, {
            id: props.activity.id,
            data: newDocs.filter((d) => !d.file).map((d, i) => d.id),
            urlSuffix: `reorder/`,
        }).then((res) => {
            if (res.statusCode === 200) {
                enqueueSnackbar(`Documents/Links reordered successfully`, { variant: 'success' });
            } else {
                enqueueSnackbar(`Documents/Links reorder failed`, { variant: 'default' });
            }
        });
    };
    const throttledSaveOntripDocumentsOrder = _.throttle(saveOntripDocumentsOrder, 1000);

    const onOntripDocumentChange = (doc, idx) => {
        onChange((docs || []).map((d, i) => (i !== idx ? d : doc)));
    };

    const onOntripDocumentSave = (doc, idx) => {
        props.setNewDocsAdded(prev => {
            if (!prev) {
                return [props.activity.id]
            }
            return [...prev, props.activity.id]
        })
        setSyncActive(doc.direction);
        const data = {
            id: doc.id,
            name: doc.name,
            url: doc.url,
            deep_link: doc.deep_link,
            is_360_degree: doc.is_360_degree,
            offset_360_degree: doc.offset_360_degree
        };
        // Announce the result only once the server has answered: the data provider resolves 4xx/5xx
        // instead of throwing, and the dialogs used to toast success before this request returned.
        const what = doc.type === 1 ? 'Document' : 'Link';
        return dataAPI(UPLOAD, resource, { id: doc.id, data, urlSuffix: `${props.activity.id}/` })
            .then((res) => {
                if (res?.statusCode === 200 && res.data) {
                    onChange((docs || []).map((d, i) => (i !== idx ? d : { ...res.data, _changed: false })));
                    enqueueSnackbar(`${what} ${doc.id ? 'updated' : 'added'} successfully`, { variant: 'success' });
                } else {
                    enqueueSnackbar(`${what} could not be saved. Please try again.`, { variant: 'error' });
                }
            })
            .catch(() => {
                enqueueSnackbar(`${what} could not be saved. Please try again.`, { variant: 'error' });
            })
            .finally(() => {
                setSyncActive(null);
                setDisableButtons(false)
            });
    };

    const onOntripDocumentOrderChange = (idx1, idx2) => {
        const newDocs = (docs || []).map((d, i) => {
            if (i === idx1) {
                return docs[idx2];
            } else if (i === idx2) {
                return docs[idx1];
            } else {
                return d;
            }
        });
        onChange(newDocs);
        throttledSaveOntripDocumentsOrder(newDocs);
    };

    const onLinkClose = () => {
        props.setDocs((prevState) => prevState.map((x) => ({ ...x, _is_open: false })));
        setDisableButtons(false)
    };

    return (
        <Grid container spacing={0} {...props.dz.getRootProps({ className: 'dropzone' })} style={{ marginLeft: isSmallScreen ? '-15px' : '47px' }}>
            {(docs || []).map((d, i) => (
                <Grid key={`${i}-${d.id}-${d.name}-${d.link}`} item xs={12}>
                    {d.type === FILE ? (
                        <CustomOntripDocument
                            showArrowButtons={props.docs.length > 1}
                            data={d}
                            idx={i}
                            open={!!d._is_open}
                            maxIdx={maxOntripDocumentIdx}
                            onDelete={props.onOntripDocumentDelete}
                            onOrderChange={onOntripDocumentOrderChange}
                            onChange={onOntripDocumentChange}
                            onSave={onOntripDocumentSave}
                            syncActive={props.syncActive}
                            isSmallScreen={props.isSmallScreen}
                            enqueueSnackbar={enqueueSnackbar}
                            classes={classes}
                            processReplaceDocumentFile={props.processReplaceDocumentFile}
                            disableButtons={disableButtons}
                            setDisableButtons={setDisableButtons}
                            disabled={props.disabled}
                        />
                    ) : (
                        <CustomOntripLink
                            showArrowButtons={props.docs.length > 1}
                            data={d}
                            open={!!d._is_open}
                            idx={i}
                            maxIdx={maxOntripDocumentIdx}
                            onDelete={props.onOntripDocumentDelete}
                            onOrderChange={onOntripDocumentOrderChange}
                            onChange={onOntripDocumentChange}
                            onSave={onOntripDocumentSave}
                            onLinkClose={onLinkClose}
                            syncActive={syncActive}
                            isSmallScreen={props.isSmallScreen}
                            existingDocs={docs}
                            enqueueSnackbar={enqueueSnackbar}
                            classes={classes}
                            disableButtons={disableButtons}
                            setDisableButtons={setDisableButtons}
                                disabled={props.disabled}
                        />
                    )}
                </Grid>
            ))}
        </Grid>
    );
};

export default CustomOntripDocumentList;
